/**
 * SchemaLibraryPage - Main page for schema management
 *
 * Displays all schemas in a DataTable with sorting and pagination. Clicking
 * a row opens the schema details dialog.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Download, Eye, Trash2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DataTable, DataTableSortHeader } from '@/components/ui/data-table';
import { useSchemas, useDeleteSchema } from '@/features/schemas/hooks/useSchemas';
import { CompatibilityBadge } from '@/features/schemas/components/CompatibilityBadge';
import { SchemaDetailsDialog } from '@/features/schemas/components/SchemaDetailsDialog';
import { useToast } from '@/hooks/useToast';
import { downloadCsv, type CsvExportColumn } from '@/lib/utils/csv-export';
import type { SchemaListItem } from '@/features/schemas/types/schema.types';

/**
 * SchemaLibraryPage Component
 */
export const SchemaLibraryPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSchemaId, setSelectedSchemaId] = useState<string | null>(null);

  // Fetch schemas - use size: 500 to load all schemas for client-side pagination
  const { data, isLoading, error, refetch } = useSchemas({ size: 500 });
  const deleteSchema = useDeleteSchema();

  const handleViewDetails = useCallback((id: string) => {
    setSelectedSchemaId(id);
  }, []);

  const handleDelete = useCallback(
    async (id: string, name: string) => {
      const confirmed = window.confirm(
        `Are you sure you want to delete schema "${name}"? This action cannot be undone.`
      );
      if (!confirmed) return;

      try {
        await deleteSchema.mutateAsync(id);
        toast({
          title: 'Schema Deleted',
          description: `Schema "${name}" has been deleted successfully`,
        });
      } catch (deleteError: any) {
        console.error('Failed to delete schema:', deleteError);
        toast({
          title: 'Delete Failed',
          description:
            deleteError.response?.data?.detail ||
            'Failed to delete schema. It may be in use by datasets.',
          variant: 'destructive',
        });
      }
    },
    [deleteSchema, toast]
  );

  const handleRefresh = useCallback(() => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'Schema list has been refreshed',
    });
  }, [refetch, toast]);

  /**
   * Filter row data based on search term
   */
  const filteredData = useMemo<SchemaListItem[]>(() => {
    if (!data?.items) return [];
    if (!searchTerm) return data.items;
    const lowerSearch = searchTerm.toLowerCase();
    return data.items.filter((schema) => schema.name.toLowerCase().includes(lowerSearch));
  }, [data, searchTerm]);

  const handleExport = useCallback(() => {
    if (!filteredData.length) {
      toast({ title: 'Nothing to export', description: 'No schemas match the current filter.' });
      return;
    }
    const csvColumns: CsvExportColumn<SchemaListItem>[] = [
      { header: 'Name', accessor: (s) => s.name },
      { header: 'Compatibility', accessor: (s) => s.compatibility_mode ?? '' },
      { header: 'Fields', accessor: (s) => s.field_count ?? '' },
      { header: 'Usage Count', accessor: (s) => (s as any).usageCount ?? 0 },
      { header: 'Created', accessor: (s) => s.created_at ?? '' },
      { header: 'Updated', accessor: (s) => s.updated_at ?? '' },
    ];
    downloadCsv(filteredData, csvColumns, `schemas_${format(new Date(), 'yyyy-MM-dd')}`);
    toast({
      title: 'Export Complete',
      description: 'Schema data exported to CSV',
    });
  }, [filteredData, toast]);

  const columns = useMemo<ColumnDef<SchemaListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => <DataTableSortHeader column={column}>Name</DataTableSortHeader>,
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
      },
      {
        accessorKey: 'compatibility_mode',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Compatibility</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.compatibility_mode;
          if (!value) return null;
          return <CompatibilityBadge mode={value} />;
        },
      },
      {
        accessorKey: 'field_count',
        header: ({ column }) => <DataTableSortHeader column={column}>Fields</DataTableSortHeader>,
        cell: ({ row }) => row.original.field_count ?? '',
        sortingFn: (a, b) => (a.original.field_count ?? 0) - (b.original.field_count ?? 0),
      },
      {
        accessorKey: 'usageCount',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Usage Count</DataTableSortHeader>
        ),
        cell: ({ row }) => (row.original as any).usageCount ?? '0',
        sortingFn: (a, b) =>
          ((a.original as any).usageCount ?? 0) - ((b.original as any).usageCount ?? 0),
      },
      {
        accessorKey: 'created_at',
        header: ({ column }) => <DataTableSortHeader column={column}>Created</DataTableSortHeader>,
        cell: ({ row }) => {
          const value = row.original.created_at;
          if (!value) return null;
          try {
            return format(new Date(value), 'MMM d, yyyy HH:mm');
          } catch {
            return value;
          }
        },
      },
      {
        accessorKey: 'updated_at',
        header: ({ column }) => <DataTableSortHeader column={column}>Updated</DataTableSortHeader>,
        cell: ({ row }) => {
          const value = row.original.updated_at;
          if (!value) return null;
          try {
            return format(new Date(value), 'MMM d, yyyy HH:mm');
          } catch {
            return value;
          }
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails(row.original.id);
              }}
              className="h-8 w-8 p-0"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.original.id, row.original.name);
              }}
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [handleViewDetails, handleDelete]
  );

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold">Schema Library</h1>
          <p className="text-muted-foreground">
            Manage schemas and data contracts for your lakehouse
          </p>
        </div>
        <Button onClick={() => navigate('/schemas/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Schema
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load schemas: {error.message || 'Unknown error'}
          </AlertDescription>
        </Alert>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-4">
        <Input
          placeholder="Search schemas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={handleExport} disabled={isLoading}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* DataTable */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">
          Loading…
        </div>
      ) : (
        <DataTable
          data={filteredData}
          columns={columns}
          onRowClick={(s) => setSelectedSchemaId(s.id)}
          initialSorting={[{ id: 'updated_at', desc: true }]}
          getRowId={(s) => s.id}
          emptyState={
            searchTerm
              ? `No schemas match "${searchTerm}".`
              : 'No schemas yet.'
          }
        />
      )}

      {/* Stats */}
      {data?.items && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing {filteredData?.length ?? 0} of {data.items.length} schemas
          </span>
          <span>Total: {data.total || data.items.length} schemas</span>
        </div>
      )}

      {/* Schema Details Dialog */}
      <SchemaDetailsDialog
        schemaId={selectedSchemaId}
        onClose={() => setSelectedSchemaId(null)}
      />
    </div>
  );
};
